import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export type ThemeMode = "dark" | "light" | "vibrant";

export const THEME_MODES: readonly ThemeMode[] = ["vibrant", "dark", "light"];

interface ThemeValue {
  theme: ThemeMode;
  setTheme: (theme: ThemeMode) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeValue | null>(null);

interface ThemeProviderProps extends PropsWithChildren {
  defaultTheme?: ThemeMode;
  storageKey?: string;
}

function isThemeMode(value: string | null): value is ThemeMode {
  return value === "light" || value === "dark" || value === "vibrant";
}

function readStoredTheme(storageKey: string, fallback: ThemeMode) {
  if (typeof window === "undefined") return fallback;
  const stored = window.localStorage.getItem(storageKey);
  return isThemeMode(stored) ? stored : fallback;
}

export function ThemeProvider({
  children,
  defaultTheme = "vibrant",
  storageKey = "starling.theme",
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<ThemeMode>(() =>
    readStoredTheme(storageKey, defaultTheme),
  );

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.dataset.theme = theme;
    document.documentElement.classList.toggle("dark", theme !== "light");
  }, [theme]);

  const value = useMemo<ThemeValue>(() => {
    const setTheme = (nextTheme: ThemeMode) => {
      setThemeState(nextTheme);
      if (typeof window !== "undefined") {
        window.localStorage.setItem(storageKey, nextTheme);
      }
    };

    return {
      theme,
      setTheme,
      toggleTheme: () => {
        const index = THEME_MODES.indexOf(theme);
        setTheme(THEME_MODES[(index + 1) % THEME_MODES.length]);
      },
    };
  }, [storageKey, theme]);

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
}
